import { api } from 'common';
import * as store from '../../../../reducers/store';

import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { AsYouType } from 'libphonenumber-js';
import { Avatar, Typography } from '@vivaahealth/design-system';
import { Col, Container, Row } from 'react-bootstrap';

import ScheduleAppointmentCard from './ScheduleAppointmentCard';

import { EmbeddedFluidContainer } from '../Common';

import { getPrevisitAsset } from '../../../../utils';

import { formatProviderSpecialties } from 'common/dist/utils/provider';
import StarRating from '../Common/StarRating';
import Profile from '../Common/Profile';

interface Props {
  provider: api.PrevisitProvider.SafeProviderWithPractices;
}

const ProviderHeader: React.FC<Props> = ({ provider }) => {
  const { firstName, lastName, prefix, suffix, previsitImageUrl } = provider;
  const embedded = useSelector((store: store.All) => store.embedded);
  const ratingWithCount = provider.rating?.reputation;
  const hasScheduleLink =
    !!provider.preference?.schedulingLink &&
    provider.preference?.schedulingLink !== '';
  const [practice] = provider.practices;
  const providerSpecialties = formatProviderSpecialties(
    provider.profile?.specialties,
  );

  const pillsAndPhoneNumber = (
    <>
      <Profile
        profile={provider.profile}
        agesServed={provider.preference?.agesServed}
      />
      {practice.phoneNumber && hasScheduleLink && (
        <PhoneNumber href={`tel:${practice.phoneNumber}`}>
          {new AsYouType('US').input(practice.phoneNumber)}
        </PhoneNumber>
      )}
    </>
  );

  const header = (
    <Row className="mx-0 pt-3 pb-0 pb-md-3">
      <StyledCol>
        <Row>
          <Col xs="auto" className="pe-3 ps-0">
            <StyledAvatar
              alt={`${prefix || ''} ${firstName} ${lastName}`}
              src={
                previsitImageUrl
                  ? getPrevisitAsset(previsitImageUrl)
                  : undefined
              }
              initials={`${firstName?.at(0)}${lastName?.at(0)}`}
            />
          </Col>
          <Col className="px-0 d-flex justify-content-center flex-column align-items-start">
            <StyledDiv className="">
              <ProviderName variant="headline-medium" bold>
                {firstName} {lastName}
                {suffix ? `, ${suffix}` : ''}
              </ProviderName>
            </StyledDiv>
            <Typography variant="title-medium">
              {!!provider.profile?.jobTitle && provider.profile.jobTitle}
            </Typography>
            <Typography variant="title-medium">
              {!!provider.profile?.jobTitle &&
                !!providerSpecialties &&
                `Specialty: ${providerSpecialties}`}
            </Typography>
            <div className="d-none d-sm-block">{pillsAndPhoneNumber}</div>
            {ratingWithCount ? (
              <StarRating ratingWithCount={ratingWithCount} />
            ) : null}
          </Col>
        </Row>
        <Row className="d-sm-none">
          <Col className="px-0">{pillsAndPhoneNumber}</Col>
        </Row>
      </StyledCol>
      <StyledCol className="d-none d-lg-flex">
        <ScheduleAppointmentCard provider={provider} isInHeader />
      </StyledCol>
    </Row>
  );
  return embedded ? (
    <EmbeddedFluidContainer fluid>{header}</EmbeddedFluidContainer>
  ) : (
    <LimitedContainer>{header}</LimitedContainer>
  );
};

export default ProviderHeader;

const StyledAvatar = styled(Avatar)`
  height: 80px !important;
  width: 80px !important;
  border-radius: 4px;
  @media (min-width: 576px) {
    height: 160px !important;
    width: 160px !important;
  }
`;

const StyledCol = styled(Col)`
  padding-top: 0.5rem;
  @media (min-width: 576px) {
    padding-top: 12px !important;
    padding-right: 0;
  }
`;

const ProviderName = styled(Typography)`
  @media (max-width: 575px) {
    font-size: 20px !important;
  }
`;

const LimitedContainer = styled(Container)`
  @media (min-width: 1200px) {
    max-width: 1032px;
  }
`;

const StyledDiv = styled.div`
  justify-content: normal;
`;

const PhoneNumber = styled.a`
  color: var(--color-content-default-dark);
  font-size: 14px;
  text-decoration: underline;

  &:hover {
    color: var(--color-content-default);
  }
`;
